import { useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import type { Locale } from '@/i18n/types';
import { useAirportCities, useAirportSearch, useStations } from '@/lib/api/airports';
import { cityOf } from '@/types/airport';
import type { AirportCity } from '@/types/airport';
import { CodeFilter } from './CodeFilter';
import type { CodeOption } from './CodeFilter';

/**
 * An airport column — origin or destination.
 *
 * Two sources, because the two questions are different sizes. An empty field offers
 * the operation's own stations, which are already in hand from
 * `/airport/with-city/country/KZ` and are what a board is usually filtered by. Typing
 * asks the server through `/airport/search/{query}`, because the full dictionary is
 * about 9 400 rows and belongs nowhere near a browser.
 *
 * The switch is at two characters. One letter would ask the server for a twenty-sixth
 * of the world on the first keystroke, and the local list already answers `A` well
 * enough; from two, the server knows about airports this operation has never flown to.
 */

const REMOTE_FROM = 2;
const MAX_OPTIONS = 100;

interface AirportFilterProps {
  /** Any of these — the column's endpoint takes a list, matched exactly. */
  values: readonly string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  ariaLabel: string;
}

export const AirportFilter = ({ values, onChange, placeholder, ariaLabel }: AirportFilterProps) => {
  const { t, locale } = useTranslation();
  const { stations, isLoading: stationsLoading } = useStations();
  const [query, setQuery] = useState('');
  const airportOf = useAirportCities(values);

  const trimmed = query.trim();
  const isRemote = trimmed.length >= REMOTE_FROM;
  const remote = useAirportSearch(isRemote ? trimmed : '');

  const found = useMemo(
    () => toOptions(isRemote ? remote.results : stations, isRemote ? '' : trimmed, locale),
    [isRemote, remote.results, stations, trimmed, locale],
  );

  // The ticked codes the list would not otherwise show — a foreign airport, when the
  // list is the stations — at the top, so anything ticked can be unticked from here.
  // Only with nothing typed: a search shows what it found.
  const extras: CodeOption[] =
    trimmed === ''
      ? values
          .filter((code) => !found.some((option) => option.code === code))
          .map((code) => {
            const airport = airportOf(code);
            return { code, label: airport ? cityOf(airport, locale) : '' };
          })
      : [];

  const shown = found.slice(0, Math.max(0, MAX_OPTIONS - extras.length));
  const options = [...extras, ...shown];
  const hidden = found.length - shown.length;

  return (
    <CodeFilter
      values={values}
      onChange={onChange}
      multiple
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      options={options}
      onQueryChange={setQuery}
      isLoading={isRemote ? remote.isSearching : stationsLoading}
      emptyLabel={remote.hasFailed ? t('schedule.airportsFailed') : t('schedule.noMatches')}
      footnote={
        // Only ever one line, and the more urgent of the two: a stale list that is
        // still being replaced says so, and a full one says what it left out.
        remote.isSearching
          ? t('schedule.searching')
          : hidden > 0
            ? t('schedule.moreMatches', { count: hidden })
            : null
      }
    />
  );
};

/**
 * Airports as options: `ALA — Almaty`.
 *
 * `filter` narrows the LOCAL list only; a server search has already done its own
 * matching and second-guessing it here would drop rows it deliberately returned.
 * Airports with no IATA code are dropped either way — a flight cannot be filtered by
 * one — and codes lead because that is what the table shows.
 */
const toOptions = (
  airports: readonly AirportCity[],
  filter: string,
  locale: Locale,
): CodeOption[] => {
  const needle = filter.trim().toUpperCase();

  return airports
    .flatMap((airport) => {
      const code = (airport.airportIataCode ?? '').toUpperCase();
      if (code === '') return [];

      const city = cityOf(airport, locale);
      if (needle !== '' && !code.startsWith(needle) && !city.toUpperCase().includes(needle)) {
        return [];
      }

      return [{ code, label: city, key: airport.id }];
    })
    // .sort((left, right) => left.code.localeCompare(right.code));
};
